import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getInvestorStatus, submitAccreditation, listOpportunities, getPortfolio,
  ACCREDITATION_OPTIONS,
  type InvestorStatus, type Opportunity, type AccreditationMethod,
} from '../../lib/investor';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoney } from '../../lib/format';
import './InvestorPages.css';

/**
 * The investor surface has three states, and which one renders is decided by the
 * ENGINE, not by this component guessing:
 *
 *   accreditation_required  no accreditation on file — show the application
 *   under_review            attested, awaiting a Voleto admin
 *   approved                the double gate is open; financials load
 *
 * Nothing financial is fetched at all in the first two states. An unapproved
 * investor gets a 403 from every opportunity route, so requesting them would only
 * manufacture an error to hide.
 */

function AccreditationForm({ onSubmitted }: { onSubmitted: (s: InvestorStatus) => void }) {
  const [method, setMethod] = useState<AccreditationMethod | ''>('');
  const [docUrl, setDocUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!method) return setError('Select the category that applies to you.');
    setBusy(true);
    setError(null);
    try {
      await submitAccreditation(method, docUrl.trim() || undefined);
      onSubmitted(await getInvestorStatus());
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not submit your application.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="bkr-card" onSubmit={submit}>
      <h2 className="bkr-card__title">Accreditation</h2>
      <p className="bkr-lede">
        Voleto&apos;s raises are offered under Regulation D 506(c), which means every
        investor must be accredited and Voleto must take reasonable steps to verify it.
        Tell us which category applies and attach supporting documentation.
      </p>

      <fieldset className="bkr-options">
        <legend className="field__label">Which applies to you?</legend>
        {ACCREDITATION_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className={`bkr-option${method === opt.value ? ' is-selected' : ''}`}
          >
            <input
              type="radio" name="accreditation" value={opt.value}
              checked={method === opt.value}
              onChange={() => setMethod(opt.value)}
            />
            <span>
              <span className="bkr-option__label">{opt.label}</span>
              <span className="bkr-option__detail">{opt.detail}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <label className="field">
        <span className="field__label">Supporting document link</span>
        <input
          className="field__input" type="url" value={docUrl}
          placeholder="https://…/verification-letter.pdf"
          onChange={(e) => setDocUrl(e.target.value)}
        />
        <span className="field__hint">
          A CPA or attorney verification letter, tax documents, or a brokerage statement.
          Voleto stores a link, we do not host uploads.
        </span>
      </label>

      {error && <p className="bkr-error" role="alert">{error}</p>}

      <div className="bkr-actions">
        <p className="bkr-hint">
          Submitting does not grant access. A Voleto admin reviews every application.
        </p>
        <button type="submit" className="button button--primary" disabled={busy || !method}>
          {busy ? 'Submitting…' : 'Submit for review'}
        </button>
      </div>
    </form>
  );
}

function PendingNotice({ status }: { status: InvestorStatus }) {
  const option = ACCREDITATION_OPTIONS.find((o) => o.value === status.accreditationMethod);
  return (
    <section className="bkr-card bkr-card--pending">
      <span className="bkr-badge bkr-badge--pending">Under review</span>
      <h2 className="bkr-card__title">Your application is with Voleto</h2>
      <p className="bkr-lede">
        We received your accreditation on{' '}
        <span className="mono">{formatDateShort(status.accreditationSubmittedAt)}</span>
        {option ? ` under the ${option.label.toLowerCase()} category` : ''}. A Voleto admin
        reviews each application by hand, you&apos;ll get access once that&apos;s done.
      </p>
      <p className="bkr-hint">
        Investment terms, funding progress and share prices stay hidden until your
        account is approved. That&apos;s deliberate: those figures are only shared with
        verified accredited investors.
      </p>
      {status.accreditationDocUrl && (
        <a className="bkr-doclink" href={status.accreditationDocUrl}
           target="_blank" rel="noreferrer noopener">
          View the document you submitted
        </a>
      )}
      <Link to="/events" className="button button--secondary bkr-selfstart">
        Browse events in the meantime
      </Link>
    </section>
  );
}

function OpportunityCard({ opportunity }: { opportunity: Opportunity }) {
  const raised = opportunity.sharesSold * opportunity.pricePerShareCents;
  const target = opportunity.totalShares * opportunity.pricePerShareCents;
  const percent = Math.round(opportunity.fundingProgress * 100);

  return (
    <article className="opp-card glass-card">
      <header className="opp-card__head">
        <div>
          <h3 className="opp-card__title">{opportunity.title}</h3>
          <p className="bkr-hint">
            {opportunity.eventTitle} · {formatDateShort(opportunity.eventDate)}
          </p>
        </div>
        {opportunity.complianceBadge === 'fully_permitted' && (
          <span className="bkr-badge bkr-badge--ok">✓ Fully permitted</span>
        )}
      </header>

      {opportunity.description && (
        <p className="opp-card__desc">{opportunity.description}</p>
      )}

      <div className="opp-progress" role="img" aria-label={`${percent}% funded`}>
        <div className="opp-progress__bar" style={{ inlineSize: `${Math.min(percent, 100)}%` }} />
      </div>

      <dl className="opp-stats">
        <div><dt>Raised</dt><dd className="mono">{formatMoney(raised)}</dd></div>
        <div><dt>Target</dt><dd className="mono">{formatMoney(target)}</dd></div>
        <div><dt>Funded</dt><dd className="mono">{percent}%</dd></div>
        <div><dt>Per share</dt><dd className="mono">{formatMoney(opportunity.pricePerShareCents)}</dd></div>
        <div><dt>Minimum</dt><dd className="mono">{opportunity.minimumShares} shares</dd></div>
        <div>
          <dt>Preferred return</dt>
          <dd className="mono">{opportunity.preferredReturnPercentage}%</dd>
        </div>
      </dl>

      <footer className="opp-card__foot">
        <span className="bkr-hint">
          <span className="mono">{opportunity.investorsCommitted}</span> investors committed
          {opportunity.daysRemaining != null && (
            <> · <span className="mono">{opportunity.daysRemaining}</span> days left</>
          )}
        </span>
        {/* This card used to carry an "Invest" button into a self-serve checkout. That
            model is gone: investments are agreed off platform and recorded by Voleto.
            A card with no action and no explanation would just read as broken. */}
        <span className="bkr-hint">
          Participation is arranged directly with Voleto. Talk to us to take part.
        </span>
      </footer>
    </article>
  );
}

export function InvestorDashboard() {
  const [status, setStatus] = useState<InvestorStatus | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[] | null>(null);
  const [portfolio, setPortfolio] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const s = await getInvestorStatus();
      setStatus(s);

      // Only fetch financial surfaces once the gate is actually open.
      if (s.approved) {
        const [opps, port] = await Promise.all([
          listOpportunities().catch(() => []),
          getPortfolio().catch(() => null),
        ]);
        setOpportunities(opps);
        setPortfolio(port as Record<string, unknown> | null);
      }
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError && err.status === 404
          ? 'No investor account found for this login.'
          : 'Could not load your investor account.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <p className="bkr-status">Loading…</p>;
  if (error || !status) {
    return <p className="bkr-status bkr-error" role="alert">{error ?? 'Unavailable.'}</p>;
  }

  return (
    <div className="bkr-page">
      <header className="bkr-header">
        <div>
          <h1 className="bkr-title display">Investor</h1>
          <p className="bkr-lede">
            {status.approved
              ? 'Open raises and your portfolio.'
              : 'Get verified to see investment terms.'}
          </p>
        </div>
        {status.approved && (
          <span className="bkr-badge bkr-badge--ok">Approved investor</span>
        )}
      </header>

      {status.state === 'accreditation_required' && (
        <AccreditationForm onSubmitted={setStatus} />
      )}

      {status.state === 'under_review' && <PendingNotice status={status} />}

      {status.approved && (
        <>
          {portfolio && (
            <section className="bkr-card">
              <h2 className="bkr-card__title">Portfolio</h2>
              <dl className="opp-stats">
                {Object.entries(portfolio)
                  .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt>{k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}</dt>
                      <dd className="mono">
                        {/^-?\d+$/.test(String(v)) && /cents/i.test(k)
                          ? formatMoney(Number(v))
                          : String(v)}
                      </dd>
                    </div>
                  ))}
              </dl>
            </section>
          )}

          <section className="bkr-section">
            <h2 className="bkr-section__title">Open raises</h2>
            {opportunities?.length === 0 && (
              <p className="bkr-hint">No open raises right now. Check back soon.</p>
            )}
            <div className="opp-list">
              {opportunities?.map((o) => <OpportunityCard key={o.id} opportunity={o} />)}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
